import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface MailAccountMissingDialogData {
  title?: string;
  message?: string;
}

@Component({
  selector: 'app-mail-account-missing-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="mail-account-dialog">
      <div class="mail-account-dialog__glow mail-account-dialog__glow--one"></div>
      <div class="mail-account-dialog__glow mail-account-dialog__glow--two"></div>

      <div class="mail-account-dialog__header">
        <div class="mail-account-dialog__icon">
          <mat-icon svgIcon="mat:mail_outline"></mat-icon>
        </div>
        <div>
          <h2>{{ data.title || 'Mail Account Not Configured' }}</h2>
          <p>{{ data.message || 'No mail ID is configured for your account. Please contact Admin or HR for assistance.' }}</p>
        </div>
      </div>

      <div class="mail-account-dialog__body">
        <div class="mail-account-dialog__card">
          <span class="mail-account-dialog__label">Status</span>
          <strong>Mailbox access unavailable</strong>
          <small>Your user profile does not have an active email configuration yet.</small>
        </div>

        <div class="mail-account-dialog__card">
          <span class="mail-account-dialog__label">Next Step</span>
          <strong>Contact Admin / HR</strong>
          <small>Ask them to configure your official mail account to enable inbox access.</small>
        </div>
      </div>

      <div class="mail-account-dialog__footer">
        <button mat-flat-button color="primary" (click)="close()">OK</button>
      </div>
    </div>
  `,
  styles: [`
    .mail-account-dialog {
      position: relative;
      overflow: hidden;
      padding: 28px;
      background:
        radial-gradient(circle at top left, rgba(14, 165, 233, 0.18), transparent 42%),
        radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.16), transparent 38%),
        linear-gradient(145deg, #f8fbff 0%, #eef4ff 100%);
      color: #16324f;
      min-width: min(520px, calc(100vw - 40px));
      max-width: 560px;
      border-radius: 28px;
      box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
    }

    .mail-account-dialog__glow {
      position: absolute;
      border-radius: 999px;
      pointer-events: none;
      filter: blur(8px);
      opacity: 0.9;
    }

    .mail-account-dialog__glow--one {
      width: 180px;
      height: 180px;
      background: rgba(14, 165, 233, 0.18);
      top: -70px;
      right: -40px;
    }

    .mail-account-dialog__glow--two {
      width: 150px;
      height: 150px;
      background: rgba(37, 99, 235, 0.15);
      bottom: -70px;
      left: -35px;
    }

    .mail-account-dialog__header,
    .mail-account-dialog__body,
    .mail-account-dialog__footer {
      position: relative;
      z-index: 1;
    }

    .mail-account-dialog__header {
      display: grid;
      grid-template-columns: 72px 1fr;
      gap: 18px;
      align-items: start;
    }

    .mail-account-dialog__icon {
      width: 72px;
      height: 72px;
      display: grid;
      place-items: center;
      border-radius: 22px;
      background: linear-gradient(160deg, #0ea5e9 0%, #2563eb 100%);
      color: #fff;
      box-shadow: 0 18px 36px rgba(37, 99, 235, 0.28);
    }

    .mail-account-dialog__icon mat-icon {
      width: 34px;
      height: 34px;
      font-size: 34px;
    }

    .mail-account-dialog__header h2 {
      margin: 0 0 8px;
      font-size: 24px;
      font-weight: 700;
      line-height: 1.2;
      letter-spacing: -0.02em;
    }

    .mail-account-dialog__header p {
      margin: 0;
      color: #4b6480;
      font-size: 14px;
      line-height: 1.6;
    }

    .mail-account-dialog__body {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      margin-top: 22px;
    }

    .mail-account-dialog__card {
      padding: 16px 18px;
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.82);
      border: 1px solid rgba(148, 163, 184, 0.22);
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
    }

    .mail-account-dialog__label {
      display: inline-block;
      margin-bottom: 8px;
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(37, 99, 235, 0.1);
      color: #1d4ed8;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .mail-account-dialog__card strong {
      display: block;
      font-size: 15px;
      font-weight: 700;
      color: #16324f;
    }

    .mail-account-dialog__card small {
      display: block;
      margin-top: 6px;
      color: #5f738b;
      font-size: 12px;
      line-height: 1.5;
    }

    .mail-account-dialog__footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 24px;
    }

    @media (max-width: 640px) {
      .mail-account-dialog {
        min-width: auto;
        padding: 22px;
        border-radius: 24px;
      }

      .mail-account-dialog__header {
        grid-template-columns: 1fr;
      }

      .mail-account-dialog__body {
        grid-template-columns: 1fr;
      }

      .mail-account-dialog__icon {
        width: 64px;
        height: 64px;
      }
    }
  `]
})
export class MailAccountMissingDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MailAccountMissingDialogData,
    private readonly dialogRef: MatDialogRef<MailAccountMissingDialogComponent>
  ) {}

  close(): void {
    this.dialogRef.close();
  }
}
